/**
 * CORP//LLM — Native WebSocket client for Go game server.
 * Connects to /ws, handles reconnect with exponential backoff,
 * and emits typed events via a simple EventEmitter.
 */

export type WSEventType = 'state_update' | 'tick_complete' | 'tick_queued' | 'tick_progress' | 'tick_completed' | 'tick_failed' | 'chat_message' | 'plan_submitted' | 'player_joined' | 'player_left' | 'player_ready' | 'session_started' | 'session_lobby' | 'notification_new' | 'pong' | 'open' | 'close';

type Listener<T = unknown> = (data: T) => void;

class EventEmitter {
  private listeners = new Map<string, Listener[]>();

  on<T>(event: string, fn: Listener<T>): () => void {
    const list = this.listeners.get(event) ?? [];
    list.push(fn as Listener);
    this.listeners.set(event, list);
    return () => this.off(event, fn as Listener);
  }

  off(event: string, fn: Listener): void {
    const list = this.listeners.get(event) ?? [];
    this.listeners.set(event, list.filter(l => l !== fn));
  }

  emit(event: string, data?: unknown): void {
    (this.listeners.get(event) ?? []).forEach(fn => fn(data));
  }
}

interface WSMessage {
  type: string;
  payload?: unknown;
}

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;

export class GameSocket extends EventEmitter {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private stopped = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly url: string) {
    super();
  }

  connect(): void {
    this.stopped = false;
    this.open();
  }

  disconnect(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.ws?.close();
    this.ws = null;
  }

  send(type: string, payload?: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ type, payload }));
  }

  private open(): void {
    if (this.stopped) return;
    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.attempt = 0;
      this.emit('open');
    };

    this.ws.onmessage = (event) => {
      try {
        const msg: WSMessage = JSON.parse(event.data as string);
        this.emit(msg.type, msg.payload);
      } catch {
        // ignore malformed messages
      }
    };

    this.ws.onclose = () => {
      this.emit('close');
      if (!this.stopped) this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(BASE_DELAY_MS * 2 ** this.attempt, MAX_DELAY_MS);
    this.attempt++;
    this.retryTimer = setTimeout(() => this.open(), delay);
  }
}

/** Resolve WebSocket URL from current window location. */
export function resolveWSUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = import.meta.env.VITE_SERVER_HOST ?? location.hostname;
  const port = import.meta.env.VITE_SERVER_PORT ?? '8080';
  return `${proto}//${host}:${port}/ws`;
}
